import { useQuery, QueryConfig, useInfiniteQuery, InfiniteQueryConfig, InfiniteQueryResult } from "react-query";
import { buildMerchantCacheKeys, buildMerchantsSharingWithMeCacheKeys } from "./utils";
import { IFetchMembersInMerhantRequest } from "../types/merchants";
import { merchants } from "../services/merchants";

export const useInfiniteFetchMembersInMerchant = (
    merchantId: string,
    params?: IFetchMembersInMerhantRequest,
    options?: Partial<InfiniteQueryConfig<nuorder.merchant.v1.ListMembersResponse, string>>
) => {
    return useInfiniteQuery<nuorder.merchant.v1.ListMembersResponse, string>(
        buildMerchantCacheKeys(undefined, merchantId, undefined),
        (_queryKey, _compositeIds, page_token: string) => {
            const newParams = {
                ...params,
                page_token,
            };
            return merchants.fetchtMerchantMembers(merchantId, newParams);
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.merchant.v1.ListMembersResponse) => {
                return lastResponse.members?.length && lastResponse.next_page_token
                    ? lastResponse.next_page_token
                    : null;
            },
        }
    );
};

export const __setMockInfiniteFetchMembershipsResults = (
    mockData: Partial<InfiniteQueryResult<nuorder.merchant.v1.ListMembersResponse, Error>>
) => {};

export const useInfiniteFetchMemberships = (
    userId: string,
    params: Partial<nuorder.merchant.v1.ListUserMembershipsRequest> = { page_size: 30 },
    options?: Partial<InfiniteQueryConfig<nuorder.merchant.v1.ListMembersResponse, Error>>
) => {
    const { page_size } = params;
    return useInfiniteQuery<nuorder.merchant.v1.ListMembersResponse, Error>(
        buildMerchantCacheKeys(userId, undefined),
        (_cacheKey, _, page_token: string) => {
            return merchants.fetchtUserMemberships(userId, {
                ...params,
                ...(page_token ? { page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.merchant.v1.ListMembersResponse) => {
                return lastResponse.members?.length &&
                    lastResponse.members.length >= page_size &&
                    lastResponse.next_page_token
                    ? lastResponse.next_page_token
                    : null;
            },
        }
    );
};

export const useFetchMerchantBrandPortals = (
    merchantBrandPortalId: string,
    options?: QueryConfig<nuorder.merchant.v1.BrandPortal>
) => {
    return useQuery<nuorder.merchant.v1.BrandPortal>(
        buildMerchantCacheKeys(undefined, undefined, merchantBrandPortalId),
        () => merchants.fetchtFetchMerchantBrandPortals(merchantBrandPortalId),
        options
    );
};

export const useFetchMembersInMerchant = (merchantId: string, options?: QueryConfig<nuorder.merchant.v1.Member[]>) => {
    return useQuery<nuorder.merchant.v1.Member[]>(
        buildMerchantCacheKeys(undefined, merchantId),
        () =>
            merchants.fetchtMerchantMembers(merchantId).then((responseJson) => {
                return responseJson.members;
            }),
        options
    );
};

export const useFetchMembershipForUser = (
    merchantId: string,
    userId: string,
    options?: QueryConfig<nuorder.merchant.v1.Member>
) => {
    return useQuery<nuorder.merchant.v1.Member>(
        buildMerchantCacheKeys(userId, merchantId),
        () => merchants.fetchMembershipByUserId(merchantId, userId),
        options
    );
};

export const useInfiniteFetchMerchants = (
    params: nuorder.merchant.v1.ListMerchantsRequest = { page_size: 30 },
    options?: Partial<InfiniteQueryConfig<nuorder.merchant.v1.ListMerchantsResponse, string>>
) => {
    const { page_size } = params;
    return useInfiniteQuery<nuorder.merchant.v1.ListMerchantsResponse, string>(
        "merchants",
        (_cacheKey, page_token: string) => {
            return merchants.fetch({
                ...params,
                ...(page_token ? { page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.merchant.v1.ListMerchantsResponse) => {
                return lastResponse.merchants?.length &&
                    lastResponse.merchants.length >= page_size &&
                    lastResponse.next_page_token
                    ? lastResponse.next_page_token
                    : null;
            },
        }
    );
};

export const useInfiniteFetchMerchantsSharingWithMe = (
    merchantId: string,
    memberId: string,
    params?: Partial<nuorder.merchant.v1.IMerchantsSharingWithMeRequest>,
    options?: Partial<InfiniteQueryConfig<nuorder.merchant.v1.IMerchantsSharingWithMeResponse, Error>>
) => {
    return useInfiniteQuery<nuorder.merchant.v1.IMerchantsSharingWithMeResponse, Error>(
        buildMerchantsSharingWithMeCacheKeys(merchantId, memberId),
        (_cacheKey, _, page_token: string) => {
            return merchants.fetchMerchantsSharingWithMe(merchantId, memberId, {
                ...params,
                ...(page_token ? { page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.merchant.v1.IMerchantsSharingWithMeResponse) => {
                return lastResponse.merchants?.length && lastResponse.next_page_token
                    ? lastResponse.next_page_token
                    : null;
            },
        }
    );
};
